import React from "react";
import InputTextIcon from "../inputs/input-text-icon";
import { urlValidate } from "../../functions/url-validate";
import { map } from "lodash";
const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 }; // US center
const DEFAULT_ZOOM = 7;

function buildDirectionsUrl(store) {
  const dest = `${store.street || ""}, ${store.city || ""}, ${store.state || ""} ${store.zip || ""}`.trim();
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}`;
}

export default class StoreLocatorComponent extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      q: "",
      radiusMiles: 50,
      center: DEFAULT_CENTER,
      results: [],
      loading: false,
      activeId: null,
      mapsReady: false,
      error: null,
      clicked:false
    };

    this._isMounted = false;
    this._map = null;
    this._markers = [];
    this._infoWindow = null;

    this._mapDivRef = null;

    this.onSubmit = this.onSubmit.bind(this);
    this.onChangeQ = this.onChangeQ.bind(this);
    this.onChangeRadius = this.onChangeRadius.bind(this);
  }

  componentDidMount() {
    this._isMounted = true;

    // Isomorphic-safe: only run this on the client.
    this.loadGoogleMapsScript()
      .then(() => {
        if (!this._isMounted) return;
        this.setState({ mapsReady: true }, () => {
          this.initMap();
        });
      })
      .catch((err) => {
        if (!this._isMounted) return;
        this.setState({ error: "Failed to load Google Maps." });
        // Optional: console for debugging
        // eslint-disable-next-line no-console
        console.error(err);
      });
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.clearMarkers();
    this._infoWindow = null;
    this._map = null;
  }

  loadGoogleMapsScript() {
    // If already loaded, resolve immediately.
    if (typeof window !== "undefined" && window.google && window.google.maps) {
      return Promise.resolve();
    }

    // Avoid double-injecting the script.
    const existing = typeof document !== "undefined"
      ? document.querySelector('script[data-google-maps="1"]')
      : null;

    if (existing) {
      return new Promise((resolve, reject) => {
        existing.addEventListener("load", () => resolve());
        existing.addEventListener("error", reject);
      });
    }

    return new Promise((resolve, reject) => {
      if (typeof document === "undefined") {
        // Server render: do nothing.
        resolve();
        return;
      }

      const apiKey = (this.props.apiKey || "").trim();
      if (!apiKey) {
        reject(new Error("Missing Google Maps apiKey prop."));
        return;
      }

      const script = document.createElement("script");
      script.async = true;
      script.defer = true;
      script.dataset.googleMaps = "1";

      // Minimal load: only maps. Add "&libraries=places" if you later add autocomplete.
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`;

      script.onload = () => resolve();
      script.onerror = (e) => reject(e);

      document.head.appendChild(script);
    });
  }

  initMap() {
    if (typeof window === "undefined") return;
    if (!window.google || !window.google.maps) return;
    if (!this._mapDivRef) return;

    this._map = new window.google.maps.Map(this._mapDivRef, {
      center: this.state.center,
      zoom: DEFAULT_ZOOM,
      fullscreenControl: false,
      streetViewControl: false,
      mapTypeControl: false
    });

    this._infoWindow = new window.google.maps.InfoWindow();
  }

  clearMarkers() {
    if (!this._markers || this._markers.length === 0) return;
    for (let i = 0; i < this._markers.length; i++) {
      this._markers[i].setMap(null);
    }
    this._markers = [];
  }

  renderMarkers(results) {
    if (typeof window === "undefined") return;
    if (!this._map) return;
    if (!window.google || !window.google.maps) return;

    this.clearMarkers();

    const self = this;

    for (let i = 0; i < results.length; i++) {
      const s = results[i];
      if (s.latitude == null || s.longitude == null) continue;

      const marker = new window.google.maps.Marker({
        map: this._map,
        position: { lat: Number(s.latitude), lng: Number(s.longitude) },
        title: s.name || ""
      });

      marker.addListener("click", function () {
        self.setActiveStoreId(s.id);
      });

      this._markers.push(marker);
    }

    // Fit bounds
    if (results.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();

      // include center
      bounds.extend(new window.google.maps.LatLng(this.state.center.lat, this.state.center.lng));

      for (let j = 0; j < results.length; j++) {
        const s2 = results[j];
        if (s2.latitude == null || s2.longitude == null) continue;
        bounds.extend(new window.google.maps.LatLng(Number(s2.latitude), Number(s2.longitude)));
      }

      this._map.fitBounds(bounds);
    }
  }

  openInfoWindowForStore(store) {
    if (typeof window === "undefined") return;
    if (!this._map || !this._infoWindow) return;

    const lat = store.latitude == null ? null : Number(store.latitude);
    const lng = store.longitude == null ? null : Number(store.longitude);
    if (lat == null || lng == null) return;

    const phoneTel = store.phone ? String(store.phone).replace(/[^0-9+]/g, "") : "";
    const content =
      `<div style="max-width:240px">
        <div style="font-weight:700;margin-bottom:6px">${escapeHtml(store.name || "")}</div>
        <div>${escapeHtml(store.street || "")}</div>
        <div>${escapeHtml(store.city || "")}, ${escapeHtml(store.state || "")} ${escapeHtml(store.zip || "")}</div>
        ${store.phone ? `<div><a href="tel:${escapeHtml(phoneTel)}">${escapeHtml(store.phone)}</a></div>` : ""}
        ${store.distanceMiles != null ? `<div style="margin-top:6px;opacity:.75">${escapeHtml(String(store.distanceMiles))} mi</div>` : ""}
      </div>`;

    this._infoWindow.setContent(content);
    this._infoWindow.setPosition({ lat, lng });
    this._infoWindow.open(this._map);
  }

  setActiveStoreId(id) {
    this.setState({ activeId: id }, () => {
      const store = this.state.results.find(r => r.id === id);
      if (!store) return;

      // pan/zoom
      if (typeof window !== "undefined" && this._map && store.latitude != null && store.longitude != null) {
        this._map.panTo({ lat: Number(store.latitude), lng: Number(store.longitude) });
        this._map.setZoom(12);
      }

      this.openInfoWindowForStore(store);
    });
  }

  onChangeQ(e) {
    this.setState({ q: e.target.value });
  }

  onChangeRadius(e) {
    const val = parseInt(e.target.value, 10);
    this.setState({ radiusMiles: isNaN(val) ? 50 : val });
  }

  async onSubmit(e) {
    e.preventDefault();

    const q = (this.state.q || "").trim();
    if (!q) return;

    this.setState({ loading: true, error: null, activeId: null });

    try {
      const url = `/api/StoreLocator?q=${encodeURIComponent(q)}&radiusMiles=${encodeURIComponent(this.state.radiusMiles)}`;
      const res = await fetch(url, { headers: { Accept: "application/json" } });
      if (!res.ok) throw new Error(`Search failed: ${res.status}`);

      const data = await res.json();

      const center = data && data.center ? data.center : DEFAULT_CENTER;
      const results = Array.isArray(data && data.results) ? data.results : [];

      if (!this._isMounted) return;

      this.setState(
        {
          center: center,
          results: results,
          clicked:true
        },
        () => {
          // Update map markers if map is ready
          if (this.state.mapsReady) {
            if (typeof window !== "undefined" && this._map && window.google && window.google.maps) {
              this._map.setCenter({ lat: Number(center.lat), lng: Number(center.lng) });
            }
            this.renderMarkers(results);
          }
        }
      );
    } catch (err) {
      if (!this._isMounted) return;
      // eslint-disable-next-line no-console
      console.error(err);
      this.setState({ error: "Could not load store results. Please try again." });
      //alert("Could not load store results. Please try again.");
    } finally {
      if (this._isMounted) {
        this.setState({ loading: false });
      }
    }
  }

  render() {
    const { q, radiusMiles, results, loading, mapsReady, error, activeId, clicked } = this.state;
    let cls='';
    if(!mapsReady){
      cls='hide';
    }
    return (
      <div className="store-locator-container">
        <div className={"store-locator-form " + cls}>
          <form onSubmit={this.onSubmit} >
            
            <InputTextIcon
              value={q}
              onChange={this.onChangeQ}
              placeholder="Enter zip code or address"
              icon="geo-position.png"
            />
 
            <select
              value={radiusMiles}
              onChange={this.onChangeRadius}
            >
              {[10, 25, 50, 100, 250].map((m) => (
                <option key={m} value={m}>
                  {m} miles
                </option>
              ))}
            </select>

            <button type="submit" disabled={loading} className="ui-button-configure form-padding ink">
              {loading ? "Searching..." : "Search"} 
              <img src="/images/icons/arrow.png" alt="arrow" className="small-arrow-right" />
            </button>

            {error ? <div style={{ color: "crimson" }}>{error}</div> : null}
          </form>

          <div className="store-locator-results">
            {clicked &&results.length === 0 ? (
              <div style={{ opacity: 0.8 }}>Your search returned no results...</div>
            ) : null}

            {results.map((s) => {
              const isActive = s.id === activeId;
              //console.log("Rendering store", s.id, "activeId=", activeId, "isActive=", isActive);
              return (
                <div
                  key={s.id}
                  onClick={() => this.setActiveStoreId(s.id)} 
                  className={"store-locator-result-item " + (isActive ? "active" : "")}
                 
                >
                  <div style={{ fontWeight: 700 }}>{s.name}</div>
                  <div>{s.street}</div>
                  <div>
                    {s.city}, {s.state} {s.zip}
                  </div>
                  {s.phone ? (
                    <div>
                      <a href={"tel:" + String(s.phone).replace(/[^0-9+]/g, "")} onClick={(e) => e.stopPropagation()}>
                        {s.phone}
                      </a>
                    </div>
                  ) : null}

                  {typeof s.distanceMiles === "number" ? (
                    <div style={{ marginTop: 6, fontSize: 13, opacity: 0.8 }}>
                      {s.distanceMiles.toFixed ? s.distanceMiles.toFixed(1) : s.distanceMiles} mi
                    </div>
                  ) : null}

                  <div style={{ marginTop: 8, display: "flex", gap: 12 }}>
                    <a
                      href={buildDirectionsUrl(s)}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Directions
                    </a>

                    {s.website ? (
                      <a href={urlValidate(s.website)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                        Website
                      </a>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ minHeight: 740 }}>
          {!mapsReady ? (
            <div className="loading-map-container">
                <div className="loading-map">Loading map…</div>
            </div>  
          ) : (
            <div
              ref={(el) => (this._mapDivRef = el)}
              style={{ width: "100%", height: "740px" }}
            />
          )}
        </div>
      </div>
    );
  }
}

// Simple HTML escape for InfoWindow content
function escapeHtml(str) {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
